import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  FormControl,
  FormLabel,
  Heading,
  Select,
  Text,
  VStack,
  useToast,
  Divider,
  Input,
  VisuallyHidden,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  Icon,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Switch
} from '@chakra-ui/react';
import { FiHelpCircle } from 'react-icons/fi';
import axios from '../../axiosConfig';
import { useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

const SurveyAcknowledgment = () => {
  const { endpoint } = useParams();
  const { t } = useTranslation();
  const [openingMessage, setOpeningMessage] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [belongsToExtraTeams, setBelongsToExtraTeams] = useState(false);
  const [selectedExtraTeam, setSelectedExtraTeam] = useState('');
  const [hasExtraTeamPages, setHasExtraTeamPages] = useState(false);

  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [honeyPot, setHoneyPot] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  // For the AlertDialog
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const cancelRef = useRef();

  // Function to clear stored answers locally and in the backend.
  const resetAnswers = async () => {
    localStorage.removeItem('survey_answers');
    const surveySession = JSON.parse(localStorage.getItem('survey_session'));
    if (surveySession && surveySession.uuid) {
      try {
        await axios.post(`/public_surveys/${endpoint}/reset_answers`, {
          uuid: surveySession.uuid,
        });
      } catch (error) {
        console.error('Error resetting answers in the backend:', error);
      }
    }
  };

  const fetchAcknowledgmentDetails = useCallback(async () => {
    try {
      const response = await axios.get(`/public_surveys/${endpoint}/acknowledgment`);
      const { opening_message, groups, roles, has_extra_team_pages } = response.data;
      setOpeningMessage(opening_message || t('public_survey.acknowledgment.defaultMessage'));
      setGroups(groups || []);
      setRoles(roles || []);
      setHasExtraTeamPages(!!has_extra_team_pages);
    } catch (error) {
      console.error('Error fetching acknowledgment details:', error);
    }
  }, [endpoint, t]);

  useEffect(() => {
    // On mount, pre-populate from localStorage "survey_session" if available.
    const surveySession = JSON.parse(localStorage.getItem('survey_session'));
    if (!surveySession?.valid) {
      navigate('../');
    }
    if (surveySession?.group && surveySession?.role) {
      setSelectedGroup(surveySession.group);
      setSelectedRole(surveySession.role);
      if (surveySession.extraTeam) {
        setBelongsToExtraTeams(true);
        setSelectedExtraTeam(surveySession.extraTeam);
      }
    }
    // Why is this being triggered again?
    fetchAcknowledgmentDetails();
  }, [fetchAcknowledgmentDetails, navigate]);

  // onChange simply updates local state
  const handleProfessionChange = (e) => {
    setSelectedGroup(e.target.value);
  };

  const handleTeamChange = (e) => {
    setSelectedRole(e.target.value);
  };

  const handleExtraTeamChange = (e) => {
    setSelectedExtraTeam(e.target.value);
  };

  // When Next is clicked, check if changes have occurred compared to the stored session.
  const handleNext = async () => {
    const surveySession = JSON.parse(localStorage.getItem('survey_session')) || {};
    // If the respondent has opted for extra teams, ensure they made a selection.
    if (belongsToExtraTeams && !selectedExtraTeam) {
      toast({
        title: t('public_survey.acknowledgment.error.title'),
        description: t('public_survey.acknowledgment.extraTeamPlaceholder', 'Please select your extra team.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    // Check if there is a change in the selections.
    if (
      surveySession.group && surveySession.role &&
      (surveySession.group !== selectedGroup || surveySession.role !== selectedRole || surveySession.extraTeam !== (belongsToExtraTeams ? selectedExtraTeam : null))
    ) {
      // Open confirmation dialog
      setIsAlertOpen(true);
    } else {
      await proceedToNextStep();
    }
  };

  // This function is called when the user confirms in the alert dialog.
  const proceedToNextStep = async () => {
    try {
      let surveySession = JSON.parse(localStorage.getItem('survey_session')) || {};
      // If selections changed, reset answers.
      if (
        surveySession.group && surveySession.role &&
        (surveySession.group !== selectedGroup || surveySession.role !== selectedRole || surveySession.extraTeam !== (belongsToExtraTeams ? selectedExtraTeam : null))
      ) {
        await resetAnswers();
      }
      // Update session with the new selections.
      surveySession.group = selectedGroup;
      surveySession.role = selectedRole;
      // Save extra team if respondent belongs to other teams.
      surveySession.extraTeam = belongsToExtraTeams ? selectedExtraTeam : null;
      localStorage.setItem('survey_session', JSON.stringify(surveySession));

      const response = await axios.post(`/public_surveys/${endpoint}/respondent`, {
        uuid: surveySession.uuid,
        group_id: selectedGroup,
        role: selectedRole,
        email: honeyPot,
        extra_teams: selectedExtraTeam
      });

      const { uuid } = response.data;
      surveySession.uuid = uuid;
      surveySession.page = 1; // Initialize global question page counter.
      localStorage.setItem('survey_session', JSON.stringify(surveySession));
      navigate('../questions');
    } catch (error) {
      toast({
        title: t('public_survey.acknowledgment.error.title'),
        description: error.response?.data?.error || t('public_survey.acknowledgment.error.description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onAlertConfirm = async () => {
    setIsAlertOpen(false);
    await proceedToNextStep();
  };

  const onAlertCancel = () => {
    setIsAlertOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && selectedGroup && selectedRole) {
      handleNext();
    }
  };

  return (
    <Box p={6} mx="auto" maxW="lg">
      <VStack spacing={6}>
        {/* Hero Section */}
        <Box
          textAlign="center"
          p={6}
          bgGradient="linear(to-r, cyan.500, teal.500)"
          borderRadius="md"
          color="white"
          boxShadow="lg"
        >
          <Heading size="lg" mb={2}>
            {t('public_survey.acknowledgment.welcome')}
          </Heading>
          <Text fontSize="md" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(openingMessage) }} />
        </Box>
        {/* Privacy Message */}
        <Box
          textAlign="center"
          p={4}
          bg="gray.100"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="sm"
          maxW="md"
        >
          <Text fontSize="sm" color="gray.600">
            {t('public_survey.acknowledgment.privacyMessage')}
          </Text>
        </Box>
        <Divider />
        {/* Group and Team Selection */}
        <Card shadow="md" border="1px" borderColor="gray.200">
          <CardHeader>
            <Heading size="md" textAlign="center" color="gray.700">
              {t('public_survey.acknowledgment.selectDetails')}
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4}>
              {/* Honeypot Field (Visually Hidden) */}
              <VisuallyHidden>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="text"
                    value={honeyPot}
                    onChange={(e) => setHoneyPot(e.target.value)}
                    autoComplete="off"
                    tabIndex="-1"
                  />
                </FormControl>
              </VisuallyHidden>
              <FormControl isRequired>
                <FormLabel>{t('public_survey.acknowledgment.selectProfession')}</FormLabel>
                <Select
                  placeholder={t('public_survey.acknowledgment.professionPlaceholder')}
                  value={selectedGroup}
                  onChange={handleProfessionChange}
                  onKeyDown={handleKeyDown}
                >
                  {groups.map((g) => (
                    <option key={g[0]} value={g[0]}>
                      {g[1]}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel display="flex" alignItems="center">
                  {t('public_survey.acknowledgment.selectRole')}
                  <Popover placement="right" closeOnBlur>
                    <PopoverTrigger>
                      <Icon as={FiHelpCircle} ml={2} />
                    </PopoverTrigger>
                    <PopoverContent
                      w={{ base: "90vw", md: "auto" }}
                      maxW={{ base: "300px", md: "auto" }}
                      p="2"
                    >
                      <PopoverArrow />
                      <PopoverCloseButton />
                      <PopoverHeader>{t('public_survey.acknowledgment.mainTeam')}</PopoverHeader>
                      <PopoverBody whiteSpace="normal" wordBreak="break-word">
                        {t('public_survey.acknowledgment.roleInfo')}
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </FormLabel>
                <Select
                  placeholder={t('public_survey.acknowledgment.rolePlaceholder')}
                  value={selectedRole}
                  onChange={handleTeamChange}
                  onKeyDown={handleKeyDown}
                >
                  {roles.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {hasExtraTeamPages && (
                <>
                  {/* Toggle for Extra Teams */}
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0">
                      {t('public_survey.acknowledgment.extraTeamsToggleLabel')}
                    </FormLabel>
                    <Switch
                      colorScheme="teal"
                      isChecked={belongsToExtraTeams}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setBelongsToExtraTeams(checked);
                        if (!checked) {
                          setSelectedExtraTeam('');
                        }
                      }}
                    />
                  </FormControl>
                  {/* Conditionally render Extra Teams Dropdown */}
                  {belongsToExtraTeams && (
                    <FormControl isRequired>
                      <FormLabel>{t('public_survey.acknowledgment.selectExtraTeam')}</FormLabel>
                      <Select
                        placeholder={t('public_survey.acknowledgment.extraTeamPlaceholder')}
                        value={selectedExtraTeam}
                        onChange={handleExtraTeamChange}
                        onKeyDown={handleKeyDown}
                      >
                        {roles.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}
            </VStack>
          </CardBody>
          <CardFooter>
            <Button
              onClick={handleNext}
              colorScheme="teal"
              variant="outline"
              width="full"
              isDisabled={!selectedGroup || !selectedRole || (belongsToExtraTeams && !selectedExtraTeam)}
              _hover={{ bg: 'teal.600', color: 'white' }}
            >
              {t('public_survey.acknowledgment.proceed')}
            </Button>
          </CardFooter>
        </Card>
      </VStack>

      {/* AlertDialog for confirming the reset */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onAlertCancel}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('public_survey.acknowledgment.changeConfirmationTitle')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('public_survey.acknowledgment.changeConfirmationMessage')}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onAlertCancel}>
                {t('public_survey.acknowledgment.cancel')}
              </Button>
              <Button colorScheme="teal" onClick={onAlertConfirm} ml={3}>
                {t('public_survey.acknowledgment.confirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default SurveyAcknowledgment;